import m from 'mithril';
import '@awesome.me/webawesome/dist/components/icon/icon.js';

import { getAuthState, subscribeAuthProfile } from '../services/authStore';
import { runtimeUrls } from '../services/env';
import { Button } from './Button';
import { Type } from './Typography';
import headerLogo from '../assets/images/headerLogo.svg';
import logo from '../assets/images/logo.svg';
import headerBg from '../assets/images/headerBG.svg';

/**
 * App shell attrs.
 */
export interface AppShellAttrs extends m.Attributes {
  /** Active page content. */
  content: m.Children;
}

const navLink = (href: string, label: string): m.Children => {
  return m(m.route.Link, {
    href,
    class: 'site-link',
  }, label);
};

const SignupButton = (): m.Children => {
  return m(Button, {
    size: 's',
    variant: 'brand',
    appearance: 'accent',
    onclick: (event) => {
      event.redraw = false;
      m.route.set('/request-invite');
    },
  }, [
    'Join the Beta',
    m('wa-icon', { slot: 'end', library: 'material', name: 'rocket_launch' }),
  ]);
};

const LaunchButton = (): m.Children => {
  return m(Button, {
    size: 's',
    variant: 'brand',
    appearance: 'accent',
    onclick: (event) => {
      event.redraw = false;
      window.open(runtimeUrls.openerURL + '/', '_blank', 'noopener');
    },
  }, [
    m('span', 'Launch MeshOS'),
    m('img', { class: 'button-logo', src: logo, alt: '' }),
  ]);
};

/**
 * Renders the shared website shell.
 */
export const AppShell: m.ClosureComponent<AppShellAttrs> = () => {
  let unsubscribe: (() => void) | null = null;
  let lastPath = '';

  return {
    oncreate: () => {
      unsubscribe = subscribeAuthProfile(m.redraw);
      lastPath = m.route.get();
      window.scrollTo({ top: 0 });
    },
    onupdate: () => {
      const path = m.route.get();
      if (path !== lastPath) {
        lastPath = path;
        window.scrollTo({ top: 0 });
      }
    },
    onremove: () => {
      unsubscribe?.();
    },
    view: ({ attrs }) => {
      const auth = getAuthState();

      return m('div', { id: 'body', class: 'app-shell' }, [
        m('header', {
          class: 'site-header',
          // headerBg may be an inlined data URI (Vite inlines SVGs under 4KB); its
          // literal parens/quotes break an unquoted url(), so quote it.
          style: { backgroundImage: `url("${headerBg}")` },
        }, [
          m(m.route.Link, { href: '/', class: 'site-header__brand' }, [
            m('img', { src: headerLogo, alt: 'MeshOS' }),
          ]),
          m('nav', { class: 'site-header__nav', 'aria-label': 'Main navigation' }, [
            m('div', { class: 'site-header__links' }, [
              auth.profile
                ? navLink('/account', 'My Account')
                : navLink('/login', 'Login'),
              auth.profile ? navLink('/download', 'Download') : null,
            ]),
            m('div', { class: 'site-header__actions' }, SignupButton()),
          ]),
        ]),
        m('main', { class: 'app-shell__main' }, attrs.content),
        m('footer', { class: 'site-footer' }, [
          m('div', { class: 'site-footer__inner' }, [
            m('div', { class: 'site-footer__brand' }, [
              m('img', { src: logo, alt: 'MeshOS Logo' }),
            ]),
            m('nav', { class: 'site-footer__col', 'aria-label': 'Footer navigation' }, [
              m(Type, { size: 'subheading' }, 'Navigate'),
              navLink('/', 'Home'),
              navLink('/account', 'My Account'),
              auth.profile ? navLink('/download', 'Download') : null,
            ]),
            m('div', { class: 'site-footer__col' }, [
              m(Type, { size: 'subheading' }, 'Say Hi'),
              m('div', { class: 'site-footer__contact' }, [
                m('wa-icon', { class: 'site-footer__icon', library: 'material', name: 'mail' }),
                m(Type, { inline: true }, 'team@meshos.com'),
              ]),
              m('div', { class: 'site-footer__contact' }, [
                m('wa-icon', { class: 'site-footer__icon', library: 'material', name: 'home' }),
                m(Type, { inline: true }, 'San Francisco, CA'),
              ]),
            ]),
            m('div', { class: 'site-footer__col site-footer__col--actions' }, [
              m(Type, { size: 'subheading' }, 'Get Started'),
              SignupButton(),
              LaunchButton(),
            ]),
          ]),
          m('p', { class: 'site-footer__copyright' }, 'Copyright © 2026 MeshOS Inc.'),
        ]),
      ]);
    },
  };
};
